import { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { orderBy, sumBy } from 'lodash';
import { UNKNOWN_CROP } from '../../constants/fields';
import { StyledNameDiv } from '../left-menu/menu-components';
import { asDynamicDecimal, asQuantity } from '../../utils/formatters';
import { AREA_UNITS, ROW_UNITS } from '../../constants/units';
import { CropSwatchPicker, Label, LabeledData } from './menu-components';
import CropVarietyIcon from '../icons/CropVarietyIcon';
import { MapContext } from '../../contexts/MapContext';
import CropUsagePanel from './CropUsagePanel';
import IconContainer from '../basics/IconContainer';

const CropVarietyData = styled(
  ({ className, cropVariety, treeCount, ...props }) => {
    return (
      <div className={className} {...props}>
        <div className="d-flex align-items-center mt-1">
          <IconContainer className="mr-2">
            <CropVarietyIcon />
          </IconContainer>
          {cropVariety?.name || 'None'}
        </div>
        <LabeledData copyable label="stems" formatter={asQuantity}>
          {treeCount}
        </LabeledData>
      </div>
    );
  },
)`
  ${LabeledData} {
    margin-left: 20px;
  }
`;

const CropVarietiesData = ({ crop, qty, varieties = [] }) => {
  const cropVarieties = useMemo(
    () =>
      varieties.map(({ cropVarietyId, treeCount }) => ({
        cropVariety: crop?.cropVarieties?.find((v) => v.id === cropVarietyId),
        treeCount,
      })),
    [varieties, crop],
  );
  const noneVarietyTreeCount = qty - sumBy(varieties, 'treeCount');

  return (
    <>
      {orderBy(cropVarieties, 'cropVariety.name').map(
        ({ cropVariety, treeCount }) => (
          <CropVarietyData
            key={cropVariety.id}
            cropVariety={cropVariety}
            treeCount={treeCount}
          />
        ),
      )}
      {!!noneVarietyTreeCount && (
        <CropVarietyData treeCount={noneVarietyTreeCount} />
      )}
    </>
  );
};

const CropData = styled(
  ({
    className,
    crop = UNKNOWN_CROP,
    qty,
    acreage,
    plantedAcreage,
    patterns,
    rowLength,
    sections,
    showVarieties = false,
    varieties = [],
    colors,
    onSelectColor,
    readonly,
  }) => (
    <div className={className}>
      <CropSwatchPicker
        crop={crop}
        colorOptions={colors}
        color="light"
        onSelect={onSelectColor}
        readonly={readonly}
      />
      <div className="d-flex justify-content-between align-items-center">
        <StyledNameDiv className="mr-1" title={crop.name}>
          {crop.name}
        </StyledNameDiv>
        <CropUsagePanel crop={crop} patterns={patterns} sections={sections} />
      </div>
      {acreage != null && acreage > 0 && (
        <LabeledData
          copyable
          label="field area"
          formatter={asDynamicDecimal}
          unitSpec={AREA_UNITS}
        >
          {acreage}
        </LabeledData>
      )}
      {!!plantedAcreage && (
        <LabeledData
          copyable
          label="planted area"
          formatter={asDynamicDecimal}
          unitSpec={AREA_UNITS}
        >
          {plantedAcreage}
        </LabeledData>
      )}
      {rowLength != null && rowLength > 0 && (
        <LabeledData
          copyable
          label="length"
          formatter={asDynamicDecimal}
          unitSpec={ROW_UNITS}
        >
          {rowLength}
        </LabeledData>
      )}
      <LabeledData copyable label="stems" formatter={asQuantity}>
        {qty}
      </LabeledData>
      {showVarieties && !!varieties.length && (
        <CropVarietiesData crop={crop} qty={qty} varieties={varieties} />
      )}
    </div>
  ),
)`
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr);
  align-items: center;
  ${CropSwatchPicker} {
    margin-left: -4px;
  }
  ${LabeledData} {
    grid-column: 2;
    margin-top: 2px;
    margin-bottom: 2px;
  }
  ${CropVarietyData} {
    grid-column: 2;
  }
  ${Label} {
    min-width: 30px;
  }
`;

export default CropData;

export const FarmCropData = ({
  className,
  crop = UNKNOWN_CROP,
  qty,
  acreage,
  plantedAcreage,
  patterns,
  rowLength,
  sections,
  showVarieties,
  varieties,
  colors,
  readonly,
}) => {
  const {
    crops: { updateCropColor },
  } = useContext(MapContext);

  return (
    <CropData
      className={className}
      crop={crop}
      qty={qty}
      acreage={acreage}
      plantedAcreage={plantedAcreage}
      patterns={patterns}
      rowLength={rowLength}
      sections={sections}
      showVarieties={showVarieties}
      varieties={varieties}
      colors={colors}
      onSelectColor={(color) => updateCropColor(crop, color)}
      readonly={readonly}
    />
  );
};
